import React, { useMemo } from "react";
import { Tag, Typography, Space, Table, Button, message } from "antd";
import {
  CalendarOutlined,
  CheckCircleOutlined,
  DollarOutlined,
  PrinterOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import THEME from "../theme";
import { formatMoney, formatDate, mergeDuePaymentWithPenalty } from "../utils";
import StyledCard from "../components/StyledCard";
import { exportDuePaymentPdf } from "../../export/DuePayment";

const { Text } = Typography;

const DuePaymentTab = ({
  duePaymentData,
  beeypupData,
  judgeData,
  loanData,
  searchData,
  loadLoanData,
  exportCardDebtor,
}) => {
  const handleDueDate = (val, record) => {
    const today = dayjs();
    const dueDate = dayjs(record.DUEDATE);
    const hasPaydate = record.PAYDATE && dayjs(record.PAYDATE).isValid();
    const useDate = hasPaydate ? dayjs(record.PAYDATE) : today;

    const isOverdue = dueDate.isBefore(useDate, "day");
    const diffDays = useDate.diff(dueDate, "day");

    return (
      <Tag
        color={isOverdue ? "red" : hasPaydate ? "green" : "default"}
        icon={
          isOverdue ? (
            <WarningOutlined />
          ) : hasPaydate ? (
            <CheckCircleOutlined />
          ) : undefined
        }
      >
        {isOverdue
          ? `เลยกำหนด ${diffDays} วัน`
          : hasPaydate
            ? `ปกติ`
            : `รอชำระ`}
      </Tag>
    );
  };

  const handleDueAmt = (val, record) => {
    const today = dayjs();
    const dueDate = dayjs(record.DUEDATE);
    const dueAmt = Number(val || 0);
    const payAmt = Number(record.PAYAMT || 0);
    const isFullyPaid = payAmt > 0 && payAmt >= dueAmt;
    const isPartiallyPaid = payAmt > 0 && payAmt < dueAmt;
    const isOverdue = dueDate.isBefore(today, "day");

    const color = isFullyPaid
      ? THEME.success
      : isPartiallyPaid
        ? THEME.info
        : isOverdue
          ? THEME.danger
          : THEME.dark;

    return (
      <Text
        style={{
          fontFamily: "monospace",
          fontSize: 14,
          color,
        }}
      >
        {formatMoney(val)}
      </Text>
    );
  };

  const mergedDuePaymentData = useMemo(
    () => mergeDuePaymentWithPenalty(duePaymentData, beeypupData),
    [duePaymentData, beeypupData],
  );

  const renderPenaltyLabel = (record) => (
    <Space size={6}>
      <WarningOutlined style={{ color: THEME.warning }} />
      <Text strong style={{ color: THEME.warning }}>
        เบี้ยปรับ งวดที่ {record.NOPAY}
      </Text>
    </Space>
  );

  const handlePrintDuePayment = async () => {
    if (!mergedDuePaymentData?.length) {
      return message.warning("ไม่มีข้อมูลตารางดิว");
    }
    try {
      let customer = loanData?.CUSTOMER;
      if (!customer) {
        const contnoForLoan =
          judgeData?.judge?.[0]?.CONTNO || searchData?.CONTNO;
        const fresh = await loadLoanData(contnoForLoan);
        customer = fresh?.CUSTOMER;
      }
      const customerName = customer
        ? `${customer?.SNAM || ""}${customer?.NAME1 || ""} ${customer?.NAME2 || ""}`.trim()
        : "";

      // รวมตารางดิว + การ์ดลูกหนี้ (ไม่รวมบันทึก) เป็นไฟล์เดียว เพื่อไม่ให้ป๊อปอัปที่ 2 ถูกบล็อก
      const pdf = await exportDuePaymentPdf({
        openWindow: false,
        contno:
          duePaymentData?.[0]?.CONTNO ||
          judgeData?.loanNew?.NEWCONTNO ||
          judgeData?.judge?.[0]?.CONTNO ||
          "",
        customerName,
        rows: mergedDuePaymentData,
      });
      await exportCardDebtor({ pdf, showNotes: false, openWindow: false });

      const url = URL.createObjectURL(pdf.output("blob"));
      const win = window.open(url);
      if (win) win.onload = () => win.print();
      else alert("กรุณาปิดการบล็อกป๊อปอัปเพื่อใช้งานฟังก์ชันนี้");
    } catch (error) {
      console.error("handlePrintDuePayment error:", error);
      message.error("เกิดข้อผิดพลาดในการสร้างเอกสาร");
    }
  };

  const paidCount =
    duePaymentData?.filter((r) => Number(r.PAYAMT) > 0)?.length || 0;
  const totalCount = duePaymentData?.length || 0;

  return (
    <>
      <StyledCard
        title="ตารางดิว"
        icon={<DollarOutlined style={{ color: THEME.danger, fontSize: 18 }} />}
        extra={
          <Space>
            <Tag color="blue" style={{ margin: 0 }}>
              ชำระแล้ว {paidCount}/{totalCount} งวด
            </Tag>
            <Button
              type="primary"
              icon={<PrinterOutlined />}
              onClick={handlePrintDuePayment}
              size="small"
            >
              พิมพ์
            </Button>
          </Space>
        }
        style={{ height: "100%" }}
      >
        <Table
          dataSource={mergedDuePaymentData}
          bordered
          scroll={{ x: "max-content" }}
          columns={[
            {
              title: "งวดที่",
              dataIndex: "NOPAY",
              key: "NOPAY",
              width: 70,
              align: "center",
              onCell: (record) =>
                record.__rowType === "penalty" ? { colSpan: 2 } : {},
              render: (val, record) => {
                if (record.__rowType === "penalty") {
                  return renderPenaltyLabel(record);
                }
                const isPaid = Number(record.PAYAMT) > 0;
                const color = isPaid ? THEME.success : THEME.primary;
                return (
                  <div
                    style={{
                      width: 28,
                      height: 28,
                      margin: "0 auto",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "50%",
                      fontWeight: 600,
                      fontSize: 13,
                      color,
                      background: `${color}15`,
                      border: `1px solid ${color}40`,
                    }}
                  >
                    {val}
                  </div>
                );
              },
            },
            {
              title: (
                <Space size={4}>
                  <CalendarOutlined /> วันครบกำหนด
                </Space>
              ),
              dataIndex: "DUEDATE",
              key: "DUEDATE",
              width: 130,
              align: "center",
              onCell: (record) =>
                record.__rowType === "penalty" ? { colSpan: 0 } : {},
              render: (val) => formatDate(val),
            },
            {
              title: (
                <Space size={4}>
                  <CheckCircleOutlined /> วันที่ชำระ
                </Space>
              ),
              dataIndex: "PAYDATE",
              key: "PAYDATE",
              width: 130,
              align: "center",
              render: (val, record) => {
                if (record.__rowType === "penalty") {
                  const isPaid = Number(record.PAY_BEEYPUP || 0) > 0;
                  return isPaid ? (
                    formatDate(record.PAYDATE)
                  ) : (
                    <Text type="secondary">-</Text>
                  );
                }
                return formatDate(val);
              },
            },
            {
              title: "สถานะ",
              dataIndex: "DELAYDAY",
              key: "DELAYDAY",
              width: 140,
              align: "center",
              render: (val, record) => {
                if (record.__rowType === "penalty") {
                  const delayDay = Number(record.DELAYDAY || 0);
                  return <Tag color="orange">ค้าง {delayDay} วัน</Tag>;
                }
                return handleDueDate(val, record);
              },
            },
            {
              title: "เงินต้นคงเหลือ",
              dataIndex: "TONBALANCE",
              key: "TONBALANCE",
              width: 120,
              align: "right",
              render: (val, record) => {
                if (record.__rowType === "penalty") {
                  return <Text type="secondary">-</Text>;
                }
                return (
                  <Text style={{ fontFamily: "monospace", fontSize: 14 }}>
                    {formatMoney(val)}
                  </Text>
                );
              },
            },
            {
              title: "เข้าต้น",
              dataIndex: "DUETONEFF",
              key: "DUETONEFF",
              width: 120,
              align: "right",
              render: (val, record) => {
                return handleDueAmt(val, record);
              },
            },
            {
              title: "เข้าดอก",
              dataIndex: "DUEINTEFF",
              key: "DUEINTEFF",
              width: 120,
              align: "right",
              render: (val, record) => {
                return handleDueAmt(val, record);
              },
            },
            {
              title: "ค่างวด",
              dataIndex: "DUEAMT",
              key: "DUEAMT",
              width: 120,
              align: "right",
              render: (val, record) => {
                if (record.__rowType === "penalty") {
                  const beeypup = Number(record.BEEYPUP || 0);
                  const payBeeypup = Number(record.PAY_BEEYPUP || 0);
                  const isFullyPaid = payBeeypup > 0 && payBeeypup >= beeypup;
                  const isPartiallyPaid =
                    payBeeypup > 0 && payBeeypup < beeypup;
                  const penaltyColor = isFullyPaid
                    ? THEME.success
                    : isPartiallyPaid
                      ? THEME.info
                      : THEME.danger;
                  return (
                    <Text
                      strong
                      style={{
                        fontFamily: "monospace",
                        fontSize: 14,
                        color: penaltyColor,
                      }}
                    >
                      {formatMoney(record.BEEYPUP)}
                    </Text>
                  );
                }
                return handleDueAmt(val, record);
              },
            },
            {
              title: "ชำระแล้ว",
              dataIndex: "PAYAMT",
              key: "PAYAMT",
              width: 120,
              align: "right",
              render: (val, record) => {
                if (record.__rowType === "penalty") {
                  const payBeeypup = Number(record.PAY_BEEYPUP || 0);
                  return (
                    <Text
                      strong
                      style={{
                        fontFamily: "monospace",
                        fontSize: 14,
                        color: payBeeypup > 0 ? THEME.success : THEME.danger,
                      }}
                    >
                      {formatMoney(payBeeypup)}
                    </Text>
                  );
                }
                return (
                  <Text
                    style={{
                      fontFamily: "monospace",
                      fontSize: 14,
                      color:
                        Number(record.PAYAMT) > 0
                          ? THEME.success
                          : THEME.danger,
                    }}
                  >
                    {formatMoney(val)}
                  </Text>
                );
              },
            },
          ]}
          rowKey="_key"
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
          }}
          size="small"
          onRow={(record) =>
            record.__rowType === "penalty"
              ? { style: { background: `${THEME.warning}12` } }
              : {
                  style: {
                    background:
                      Number(record.PAYAMT) > 0
                        ? `${THEME.success}08`
                        : undefined,
                  },
                }
          }
          summary={() => {
            const totalDue = duePaymentData?.reduce(
              (sum, r) => sum + Number(r.DUEAMT || 0),
              0,
            );
            const totalPaid = duePaymentData?.reduce(
              (sum, r) => sum + Number(r.PAYAMT || 0),
              0,
            );
            const totalTonEff = duePaymentData?.reduce(
              (sum, r) => sum + Number(r.DUETONEFF || 0),
              0,
            );
            const totalIntEff = duePaymentData?.reduce(
              (sum, r) => sum + Number(r.DUEINTEFF || 0),
              0,
            );
            const remaining = totalDue - totalPaid;
            const totalPenaltyCharged = beeypupData?.reduce(
              (sum, r) => sum + Number(r.BEEYPUP || 0),
              0,
            );
            const totalPenaltyPaid = beeypupData?.reduce(
              (sum, r) => sum + Number(r.PAY_BEEYPUP || 0),
              0,
            );
            const remainingPenalty = totalPenaltyCharged - totalPenaltyPaid;

            return (
              <>
                <Table.Summary.Row style={{ background: THEME.light }}>
                  <Table.Summary.Cell colSpan={5}>
                    <Text strong>รวมทั้งหมด</Text>
                  </Table.Summary.Cell>

                  <Table.Summary.Cell align="right">
                    <Text
                      strong
                      style={{ fontFamily: "monospace", fontSize: 14 }}
                    >
                      {formatMoney(totalTonEff)}
                    </Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell align="right">
                    <Text
                      strong
                      style={{ fontFamily: "monospace", fontSize: 14 }}
                    >
                      {formatMoney(totalIntEff)}
                    </Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell align="right">
                    <Text
                      strong
                      style={{ fontFamily: "monospace", fontSize: 14 }}
                    >
                      {formatMoney(totalDue)}
                    </Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell align="right">
                    <Text
                      strong
                      style={{
                        color: totalPaid > 0 ? THEME.success : THEME.danger,
                        fontSize: 14,
                        fontFamily: "monospace",
                      }}
                    >
                      {formatMoney(totalPaid)}
                      <div>
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          คงเหลือ : {formatMoney(remaining)}
                        </Text>
                      </div>
                    </Text>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
                <Table.Summary.Row style={{ background: `${THEME.warning}10` }}>
                  <Table.Summary.Cell colSpan={6}>
                    <Space size={6}>
                      <WarningOutlined style={{ color: THEME.warning }} />
                      <Text strong style={{ color: THEME.warning }}>
                        รวมเบี้ยปรับ
                      </Text>
                    </Space>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell align="right">
                    <Text
                      strong
                      style={{
                        fontFamily: "monospace",
                        fontSize: 14,
                        color:
                          remainingPenalty > 0 ? THEME.danger : THEME.success,
                      }}
                    >
                      {formatMoney(remainingPenalty)}
                    </Text>
                    <div>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        คงเหลือ
                      </Text>
                    </div>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell align="right">
                    <Text
                      strong
                      style={{
                        fontFamily: "monospace",
                        fontSize: 14,
                        color: THEME.warning,
                      }}
                    >
                      {formatMoney(totalPenaltyCharged)}
                    </Text>
                    <div>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        ถูกคิด
                      </Text>
                    </div>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell align="right">
                    <Text
                      strong
                      style={{
                        fontFamily: "monospace",
                        fontSize: 14,
                        color:
                          totalPenaltyPaid > 0 ? THEME.success : THEME.danger,
                      }}
                    >
                      {formatMoney(totalPenaltyPaid)}
                    </Text>
                    <div>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        ชำระแล้ว
                      </Text>
                    </div>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </>
            );
          }}
        />
      </StyledCard>
    </>
  );
};

export default DuePaymentTab;
